import {
  type APIPinnedMessage,
  can,
  PermissionFlags,
} from '@alternativamais/chat-core';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { avisarErro } from '../../../componentes/Avisos';
import { Icone } from '../../../componentes/Icone';
import { useCliente, useEstado } from '../../../chat/contexto';
import { rotaDoCanal } from '../Canal';
import {
  BotaoIr,
  CaixaPopover,
  Carregando,
  MensagemResumida,
  Vazio,
} from '../componentes';

/** Mensagens fixadas do canal (C8): ir até ela e desafixar. */
export function Fixadas({
  canalId,
  aoFechar,
}: {
  canalId: string;
  aoFechar: () => void;
}) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const canal = useEstado((estado) => estado.channels[canalId]);
  const podeFixar = useEstado(
    (estado) =>
      !estado.channels[canalId]?.guild_id ||
      can(estado, canalId, PermissionFlags.PIN_MESSAGES) ||
      can(estado, canalId, PermissionFlags.MANAGE_MESSAGES),
  );
  const [itens, setItens] = useState<APIPinnedMessage[] | null>(null);
  const [mais, setMais] = useState(false);

  useEffect(() => {
    let vivo = true;
    cliente.api
      .pins(canalId, { limit: 50 })
      .then((resposta) => {
        if (!vivo) return;
        setItens(resposta.items);
        setMais(resposta.has_more);
      })
      .catch((erro) => {
        avisarErro(erro);
        if (vivo) setItens([]);
      });
    return () => {
      vivo = false;
    };
  }, [cliente, canalId]);

  const carregarMais = () => {
    const ultimo = itens?.at(-1);
    if (!ultimo) return;
    cliente.api
      .pins(canalId, { before: ultimo.pinned_at, limit: 50 })
      .then((resposta) => {
        setItens((atuais) => [...(atuais ?? []), ...resposta.items]);
        setMais(resposta.has_more);
      })
      .catch(avisarErro);
  };

  const desafixar = (mensagemId: string) => {
    cliente.api
      .unpin(canalId, mensagemId)
      .then(() =>
        setItens((atuais) =>
          (atuais ?? []).filter((item) => item.message.id !== mensagemId),
        ),
      )
      .catch(avisarErro);
  };

  return (
    <CaixaPopover
      titulo="Mensagens fixadas"
      icone="pin"
      aoFechar={aoFechar}
      rodape={
        canal?.guild_id ? (
          <p className="m-0 shrink-0 border-t border-borda px-4 py-2.5 text-xs text-texto-3">
            Fixar exige a permissão Fixar mensagens no canal.
          </p>
        ) : undefined
      }
    >
      {itens === null ? (
        <Carregando />
      ) : itens.length === 0 ? (
        <Vazio
          icone="pin"
          titulo="Nenhuma mensagem fixada"
          texto="Fixe as mensagens importantes pelo menu da mensagem."
        />
      ) : (
        <div className="flex flex-col gap-2 p-3">
          {itens.map((item) => (
            <MensagemResumida
              key={item.message.id}
              mensagem={item.message}
              guildId={canal?.guild_id ?? null}
              acoes={
                <>
                  <BotaoIr
                    aoClicar={() => {
                      aoFechar();
                      if (canal)
                        navegar(`${rotaDoCanal(canal)}/${item.message.id}`);
                    }}
                  />
                  {podeFixar && (
                    <button
                      type="button"
                      aria-label="Desafixar mensagem"
                      title="Desafixar"
                      onClick={() => desafixar(item.message.id)}
                      className="flex size-6 items-center justify-center rounded-md bg-ativo text-texto-2 hover:text-ocupado"
                    >
                      <Icone nome="x" tamanho={14} />
                    </button>
                  )}
                </>
              }
            />
          ))}
          {mais && (
            <button
              type="button"
              onClick={carregarMais}
              className="h-9 rounded-lg text-sm font-semibold text-link hover:underline"
            >
              Ver mais
            </button>
          )}
        </div>
      )}
    </CaixaPopover>
  );
}
